import { useEffect } from 'react'

import { autorun, isObservable } from 'mobx'

/**
 * createAutorun
 * =============
 *
 * This hook allows us to generate side effects from observables.
 * It accepts a mix of observable and not observable values and it will
 * succesfully trigger only one one of them changes.
 *
 *   ```js
 *   import createAutorun from 'modules/core/hooks/createAutorun'
 *
 *   const useLogger = createAutorun((users, name) => {
 *     console.log(users.filter({ name }).toJS())
 *   })
 *
 *   const Users = ({ users, name }) => {
 *     useLogger(users, name)
 *
 *     return (
 *       <ul>
 *         {users.map(u =>
 *           <li>{u.get('name') === name)}</li>
 *         )}
 *       </ul>
 *     )
 *   }
 *   ```
 */
type Callback = (...args: any[]) => void
interface AutorunOptions {
  name?: string
  delay?: number
}

const createAutorun =
  (fn: Callback, options: AutorunOptions = {}): Callback =>
  (...args) => {
    useEffect(
      () =>
        autorun(() => {
          fn(...args)
        }, options),
      args.filter((a) => !isObservable(a)) // eslint-disable-line react-hooks/exhaustive-deps
    )
  }

export default createAutorun
